import React, {Children} from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import {Switch} from '@tenancy.nz/ui';
import AnimateStack from './animate-stack';
import styles from './aside.styled';

const transition = {
  duration: 0.28,
  ease: [0.77, 0.2, 0.05, 1],
};

const variants = {
  open: {width: 240, transition},
  collapsed: {width: 68, transition},
};

const mobileVariants = {
  open: {
    opacity: 1,
    width: '100vw',
    height: 'calc(100vh - 75px)',
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 16,
      mass: 0.9,
      opacity: {duration: 0.2},
    },
  },
  collapsed: {
    opacity: 0,
    width: '100vw',
    height: 0,
    y: -3,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 32,
      opacity: {duration: 0.15},
    },
  },
};

function Aside({
  children,
  collapsed,
  switchOn,
  isMobile,
  onHover,
  onToggleCollapsed,
}) {
  const [navigation, partnerList, logoutButton] = Children.toArray(children);
  const variant = collapsed ? 'collapsed' : 'open';
  const asideVariants = isMobile ? mobileVariants : variants;

  const header = isMobile ? null : (
    <Stack
      width="68px"
      height="54px"
      alignItems="center"
      justifyContent="center">
      <Switch
        color="tertiary"
        checked={switchOn}
        onChange={onToggleCollapsed}
      />
    </Stack>
  );

  return (
    <AnimateStack
      component="aside"
      variants={asideVariants}
      initial={isMobile ? 'collapsed' : false}
      animate={variant}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      sx={[styles, isMobile && collapsed && {pointerEvents: 'none'}]}>
      <Stack>
        {header}
        {navigation}
      </Stack>
      <AnimateStack
        gap={4}
        animate={{
          paddingLeft: collapsed ? 0 : 16,
          paddingRight: collapsed ? 0 : 16,
        }}
        transition={transition}
        sx={{
          mt: 'auto',
          pt: 6,
          pb: {xs: 10, md: 6},
          borderTop: '1px solid',
          borderTopColor: 'grey.300',
          width: '100%',
          flexShrink: 0,
        }}>
        {partnerList}
        {logoutButton && <Stack mt={6}>{logoutButton}</Stack>}
      </AnimateStack>
    </AnimateStack>
  );
}

Aside.propTypes = {
  children: PropTypes.node,
  collapsed: PropTypes.bool.isRequired,
  switchOn: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  onHover: PropTypes.func.isRequired,
  onToggleCollapsed: PropTypes.func.isRequired,
};

Aside.defaultProps = {
  children: null,
};

export default Aside;
